import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { TrendingUp, Users, FileText, Calendar, RefreshCw } from 'lucide-react';

const AdminPanel = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalFixes: 0,
    totalUsers: 0,
    totalCharacters: 0,
    todayFixes: 0
  });
  const [grammarFixes, setGrammarFixes] = useState([]);
  const [selectedText, setSelectedText] = useState(null);

  useEffect(() => {
    // Security check: only allow admin access
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (userProfile && !userProfile.isAdmin) {
      navigate('/');
      return;
    }

    if (userProfile && userProfile.isAdmin) {
      loadAdminData();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(() => {
        loadAdminData(true);
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [currentUser, userProfile, navigate]);

  const loadAdminData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Load grammar fixes - try without orderBy first if that fails
      let fixesData = [];
      try {
        const fixesQuery = query(
          collection(db, 'grammarFixes'),
          orderBy('timestamp', 'desc'),
          limit(100)
        );
        const fixesSnapshot = await getDocs(fixesQuery);
        fixesData = fixesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (orderError) {
        console.log('OrderBy failed, trying without ordering:', orderError);
        // Try without orderBy if index doesn't exist
        const fixesSnapshot = await getDocs(collection(db, 'grammarFixes'));
        fixesData = fixesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }
      
      setGrammarFixes(fixesData);

      // Load users count
      let totalUsers = 0;
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        totalUsers = usersSnapshot.size;
      } catch (userError) {
        console.log('Error loading users:', userError);
      }

      // Calculate stats
      const totalFixes = fixesData.length;
      const totalCharacters = fixesData.reduce((sum, fix) => sum + (fix.characterCount || 0), 0);
      
      const today = new Date().toDateString();
      const todayFixes = fixesData.filter(fix => {
        const fixDate = fix.timestamp?.toDate?.();
        return fixDate && fixDate.toDateString() === today;
      }).length;

      setStats({
        totalFixes,
        totalUsers,
        totalCharacters,
        todayFixes
      });

      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error('Error loading admin data:', error);
      setError(error.message || 'Failed to load admin data');
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 500);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (!userProfile || !userProfile.isAdmin) {
    return (
      <div className="admin-panel" style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You do not have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-panel" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loader"></div>
        <p>Loading admin data...</p>
        {error && (
          <div style={{ color: 'red', marginTop: '20px', padding: '10px', background: '#fee' }}>
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="admin-panel" style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error Loading Admin Panel</h2>
        <p style={{ color: 'red' }}>{error}</p>
        <button onClick={() => loadAdminData()} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="admin-panel" style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div className="admin-header" style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '32px', marginBottom: '10px' }}>Admin Dashboard</h1>
          <p style={{ color: '#666' }}>Monitor grammar fixes and user activity (includes both auth & guest users)</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            padding: '12px 24px',
            background: refreshing ? '#ccc' : 'var(--primary-color)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          <RefreshCw size={18} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Stats Cards */}
      <div className="stats-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px',
        marginBottom: '40px'
      }}>
        <div className="stat-card" style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <FileText size={40} />
            <div>
              <div style={{ fontSize: '14px', opacity: '0.9' }}>Total Fixes</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{stats.totalFixes}</div>
            </div>
          </div>
        </div>

        <div className="stat-card" style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <Calendar size={40} />
            <div>
              <div style={{ fontSize: '14px', opacity: '0.9' }}>Today's Fixes</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{stats.todayFixes}</div>
            </div>
          </div>
        </div>

        <div className="stat-card" style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <Users size={40} />
            <div>
              <div style={{ fontSize: '14px', opacity: '0.9' }}>Total Users</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{stats.totalUsers}</div>
            </div>
          </div>
        </div>

        <div className="stat-card" style={{
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <TrendingUp size={40} />
            <div>
              <div style={{ fontSize: '14px', opacity: '0.9' }}>Total Characters</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
                {stats.totalCharacters.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grammar Fixes Table */}
      <div className="fixes-section">
        <h2 style={{ fontSize: '24px', marginBottom: '20px' }}>Recent Grammar Fixes</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            background: 'var(--card-bg)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{ background: 'var(--primary-color)', color: 'white' }}>
                <th style={{ padding: '15px', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>User</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Original Text</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Corrected Text</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>Characters</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {grammarFixes.map((fix, index) => (
                <tr key={fix.id} style={{ 
                  borderBottom: '1px solid var(--border-color)',
                  background: index % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)'
                }}>
                  <td style={{ padding: '12px', fontSize: '13px' }}>
                    {formatDate(fix.timestamp)}
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px' }}>
                    {fix.userEmail}
                  </td>
                  <td style={{ padding: '12px', maxWidth: '300px' }}>
                    <div style={{ 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      fontSize: '13px'
                    }}>
                      {fix.originalText}
                    </div>
                  </td>
                  <td style={{ padding: '12px', maxWidth: '300px' }}>
                    <div style={{ 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      fontSize: '13px'
                    }}>
                      {fix.correctedText}
                    </div>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px' }}>
                    {fix.characterCount || 0}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => setSelectedText(fix)}
                      style={{
                        padding: '6px 12px',
                        background: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      View Full
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for viewing full text */}
      {selectedText && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setSelectedText(null)}
        >
          <div 
            style={{
              background: 'var(--card-bg)',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '20px' }}>Grammar Fix Details</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <strong>User:</strong> {selectedText.userEmail}<br />
              <strong>Date:</strong> {formatDate(selectedText.timestamp)}<br />
              <strong>Characters:</strong> {selectedText.characterCount}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px' }}>Original Text:</h4>
              <div style={{
                padding: '15px',
                background: 'rgba(255,0,0,0.05)',
                borderLeft: '4px solid #f5576c',
                borderRadius: '4px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {selectedText.originalText}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px' }}>Corrected Text:</h4>
              <div style={{
                padding: '15px',
                background: 'rgba(0,255,0,0.05)',
                borderLeft: '4px solid #43e97b',
                borderRadius: '4px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {selectedText.correctedText}
              </div>
            </div>

            <button
              onClick={() => setSelectedText(null)}
              style={{
                padding: '10px 20px',
                background: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
